import type { JoinInviteResponse } from '@hiredude/meet-shared';
import { Share2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { GUEST_LINK_DESCRIPTION, JoinLinkRow } from '@/components/admin/joinLink';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { fetchInvite, MeetApiError } from '@/lib/api';

/**
 * Sends the candidate their link from inside the call.
 *
 * The link is fetched rather than derived, because the browser never sees the
 * guest's key — the host holds only its own. Fetching also means the host
 * cannot accidentally hand over the host link, which would give a candidate
 * the power to mute and remove the interviewer.
 */
export function ShareDialog({
  open,
  onOpenChange,
  code,
  joinKey,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  code: string;
  joinKey: string;
}) {
  const [invite, setInvite] = useState<JoinInviteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetched on open rather than on mount: most calls never open this, and a
  // request per call for a link nobody asked for is a request wasted.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setError(null);

    fetchInvite(code, joinKey)
      .then((result) => {
        if (!cancelled) setInvite(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof MeetApiError ? err.message : 'Could not load the invite link.',
        );
      });

    return () => {
      cancelled = true;
    };
  }, [open, code, joinKey]);

  // Phones put a real share sheet behind this; desktops mostly do not, so the
  // copy button stays the primary path and this is offered only when it works.
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const nativeShare = () => {
    if (!invite) return;
    void navigator
      .share({
        title: 'Your interview link',
        text: `Join the interview (${invite.code})`,
        url: invite.url,
      })
      .catch(() => undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share this meeting</DialogTitle>
          <DialogDescription>
            {invite
              ? `Send this to ${invite.name}. It is their link, not yours — opening it yourself would join as them.`
              : 'The candidate’s own link, so you can re-send it without leaving the call.'}
          </DialogDescription>
        </DialogHeader>

        {error ? (
          <p
            role="alert"
            className="text-sm text-[color-mix(in_oklab,var(--destructive)_80%,white)] text-pretty"
          >
            {error}
          </p>
        ) : invite ? (
          <div className="space-y-3">
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <span className="font-semibold tracking-[0.08em] uppercase">Meeting code</span>
              <span className="font-mono text-sm tracking-wide select-all">{invite.code}</span>
            </div>

            <JoinLinkRow
              label={invite.name}
              description={GUEST_LINK_DESCRIPTION}
              url={invite.url}
              openLabel="Open the candidate link in a new tab"
            />
          </div>
        ) : (
          <div className="flex items-center gap-2 py-6 text-sm">
            <Spinner className="size-4" />
            <span className="text-muted-foreground">Fetching the invite link…</span>
          </div>
        )}

        <DialogFooter>
          {canNativeShare && invite && (
            <Button variant="outline" onClick={nativeShare}>
              <Share2 />
              Share…
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
